/**
 * List collection detail provider.
 *
 * Provides list-collection information, the lists in the collection, and thin mutation wrappers.
 */
import {
  createContext,
  useContext as useReactContext,
  useMemo,
  type PropsWithChildren,
} from "react";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { Zero, Row, PromiseWithServerResult } from "@rocicorp/zero";
import { mutators, queries, schema } from "@corpuscomplete/core/zero";
import { ListCollection } from "@corpuscomplete/core/common/list-collection";
import { List } from "@corpuscomplete/core/common/list";
import { AuthUI } from "../../../auth";

type CollectionRow = Row<typeof schema.tables.listCollection>;

export namespace ListCollectionDetail {
  export type Context = {
    collectionId: string;
    userId: string;
  };

  const DataContext = createContext<Context | null>(null);

  export type DataProviderProps = PropsWithChildren<{
    collectionId: string;
    userId: string;
  }>;

  export function DataProvider({ children, collectionId, userId }: DataProviderProps) {
    return <DataContext value={{ collectionId, userId }}>{children}</DataContext>;
  }

  export function useContext(): Context {
    const context = useReactContext(DataContext);
    if (!context) {
      throw new Error(
        "ListCollectionDetail.useContext must be used within ListCollectionDetail.DataProvider",
      );
    }
    return context;
  }

  export type CollectionInfo = CollectionRow & {
    isDefault: boolean;
  };

  export function useCollection(): CollectionInfo | undefined {
    const { collectionId } = useContext();
    const [collections] = useQuery(queries.listCollection.forRootManagement({ limit: 50 }));

    return useMemo(() => {
      const collection = collections.find((c) => c.id === collectionId);
      if (!collection) return undefined;
      return {
        ...collection,
        isDefault: collection.defaultForUserId !== null,
      };
    }, [collections, collectionId]);
  }

  export function useLists(): List.Preview[] {
    const { collectionId } = useContext();
    const [allLists] = useQuery(queries.list.all());

    return useMemo(
      () => allLists.filter((list) => list.collectionId === collectionId),
      [allLists, collectionId],
    );
  }

  export type Permissions = {
    canRename: boolean;
    canDelete: boolean;
    canAddContent: boolean;
  };

  export function usePermissions(): Permissions {
    const { collectionId } = useContext();
    return AuthUI.usePermissions(ListCollection.Permissions, collectionId);
  }

  export type DomainConstraints = {
    isRenameable: boolean;
    isDeletable: boolean;
  };

  export function useDomainConstraints(): DomainConstraints {
    const collection = useCollection();
    const lists = useLists();

    return useMemo(() => {
      if (!collection) {
        return {
          isRenameable: false,
          isDeletable: false,
        };
      }

      const { isDefault } = collection;
      const isEmpty = lists.length === 0;

      return {
        isRenameable: !isDefault,
        isDeletable: !isDefault && isEmpty,
      };
    }, [collection, lists.length]);
  }

  export type Mutations = {
    rename: (name: string) => PromiseWithServerResult;
    del: () => PromiseWithServerResult;
  };

  export function useMutations(): Mutations {
    const z = useZero();
    const { collectionId } = useContext();

    return useMemo(
      () => ({
        rename: (name) => z.mutate(mutators.listCollection.rename({ id: collectionId, name })),
        del: () => z.mutate(mutators.listCollection.delete({ id: collectionId })),
      }),
      [z, collectionId],
    );
  }

  export function useLoadingState(): { isLoading: boolean } {
    const [, collectionDetails] = useQuery(queries.listCollection.forRootManagement({ limit: 50 }));
    const [, listsDetails] = useQuery(queries.list.all());

    return {
      isLoading: collectionDetails.type === "unknown" || listsDetails.type === "unknown",
    };
  }

  export function preload(zero: Zero, _collectionId: string): void {
    void zero.run(queries.listCollection.forRootManagement({ limit: 50 }));
    void zero.run(queries.list.all());
  }
}
